import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API from '../services/config/api';
import AuthLayout, { trackPointer, Field, AlertCircleIcon, CheckCircleIcon, ArrowRightIcon, LockIcon, EyeButton } from '../Auth/AuthLayout';

function InternalAuditResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [form, setForm] = useState({ password: '', confirm: '' });
    const [showPass, setShowPass] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const redirectTimer = useRef(null);

    useEffect(() => () => clearTimeout(redirectTimer.current), []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.password || !form.confirm) { setError('Both fields are required.'); return; }
        if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
        if (form.password !== form.confirm) { setError('Passwords do not match.'); return; }
        if (!token) { setError('Invalid or missing reset token. Please request a new reset link.'); return; }
        setLoading(true); setError('');
        try {
            const axios = (await import('axios')).default;
            await axios.post(
                API.AUDIT_PORTAL_RESET_PASSWORD,
                { token, password: form.password, confirm_password: form.confirm },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'accept': 'application/json',
                        'Authorization': 'Basic YmFzZWFkbWluOmRlZmF1bHRANDU2',
                    },
                }
            );
            setSuccess(true);
            redirectTimer.current = setTimeout(() => navigate('/audit-portal/login'), 3000);
        } catch (e) {
            const msg = e?.response?.data?.response_data?.message || e?.response?.data?.error?.message || '';
            setError(msg || 'Failed to reset password. The link may have expired.');
        }
        setLoading(false);
    };

    return (
        <AuthLayout>
            <AuthLayout.Stage/>
            <AuthLayout.Panel>
                <div className="lg-wrap">
                    <header className="lg-form-header">
                        <p className="lg-eyebrow"><LockIcon/> Audit portal</p>
                        <h1 className="lg-title">Reset password</h1>
                        <p className="lg-lead">Enter your new password below.</p>
                    </header>

                    {!token && (
                        <div className="lg-alert lg-alert--error" role="alert">
                            <AlertCircleIcon/> <span>No reset token found. Please use the link from your email.</span>
                        </div>
                    )}

                    {success ? (
                        <div className="lg-alert lg-alert--success" role="status">
                            <CheckCircleIcon/> <span>Password reset successfully. Redirecting to login...</span>
                        </div>
                    ) : (
                        <form className="lg-form" noValidate onSubmit={handleSubmit}>
                            {error && (
                                <div className="lg-alert lg-alert--error" role="alert">
                                    <AlertCircleIcon/> <span>{error}</span>
                                </div>
                            )}

                            <Field
                                name="password"
                                type={showPass ? 'text' : 'password'}
                                label="New password"
                                icon={<LockIcon/>}
                                value={form.password}
                                onChange={e => { setForm(prev => ({ ...prev, password: e.target.value })); setError(''); }}
                                autoComplete="new-password"
                                trailing={<EyeButton visible={showPass} onToggle={() => setShowPass(v => !v)}/>}
                            />

                            <Field
                                name="confirm"
                                type={showConfirm ? 'text' : 'password'}
                                label="Confirm password"
                                icon={<LockIcon/>}
                                value={form.confirm}
                                onChange={e => { setForm(prev => ({ ...prev, confirm: e.target.value })); setError(''); }}
                                autoComplete="new-password"
                                trailing={<EyeButton visible={showConfirm} onToggle={() => setShowConfirm(v => !v)}/>}
                            />

                            <button type="submit" className="lg-submit lg-spot"
                                disabled={loading || !token} aria-busy={loading} onPointerMove={trackPointer}>
                                {loading ? (
                                    <span className="lg-submit-label"><span className="lg-spin" aria-hidden="true"/> Resetting...</span>
                                ) : (
                                    <span className="lg-submit-label">Reset password <ArrowRightIcon/></span>
                                )}
                                {loading && <i className="lg-scan" aria-hidden="true"/>}
                            </button>
                        </form>
                    )}

                    <p className="lg-signup">
                        <button type="button" className="lg-link" onClick={() => navigate('/audit-portal/login')}>
                            ← Back to login
                        </button>
                    </p>
                </div>

                <p className="lg-trust">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="4.5" y="10.5" width="15" height="10" rx="3"/>
                        <path d="M8 10.5V8a4 4 0 018 0v2.5"/>
                    </svg>
                    Protected by enterprise-grade security. Foresee Security - Internal Audit Portal
                </p>
            </AuthLayout.Panel>
        </AuthLayout>
    );
}

export default InternalAuditResetPassword;
